import { ClipboardList, History, Settings } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Breadcrumb, type BreadcrumbSegment } from '../../../../components/Breadcrumb';
import { CountBadge } from '../../../../components/CountBadge';
import { FolderTabs } from '../../../../components/FolderTabs';
import { Tooltip } from '../../../../components/Tooltip';
import { ErrorState } from '../../../../components/ErrorState';
import { Menu } from '../../../../components/Menu';
import { PreviewTable } from '../../../../components/PreviewTable';
import { Skeleton } from '../../../../components/Skeleton';
import { HistoryLogsModal } from '../../../../components/index-page/HistoryLogsModal';
import { ProfileCard } from '../../../../components/profile/ProfileCard';
import { ProfileHeader } from '../../../../components/profile/ProfileHeader';
import { ProfileHeaderShell } from '../../../../components/profile/ProfileHeaderShell';
import { StatusText } from '../../../../components/table/StatusText';
import { ApiError } from '../../../../lib/apiError';
import { formatDateTime, toIso } from '../../../../lib/formatDate';
import { SurveyModal } from '../components/SurveyModal';
import { SurveyQuestionnairesPanel } from '../components/SurveyQuestionnairesPanel';
import { SurveyDevelopmentPlanPanel } from '../components/SurveyDevelopmentPlanPanel';
import { SurveyResponsesPanel, type ResponsesKey } from '../components/SurveyResponsesPanel';
import { SurveyStatusCard } from '../components/SurveyStatusCard';
import { surveysApi } from '../surveyApi';
import type { Survey } from '../surveyTypes';
import { describeTargets } from '../utils/describeTargets';
import { nextAction, type ConfigurationKey, type TabKey } from '../utils/surveyNextAction';
import { SurveyNextActionBanner } from '../components/SurveyNextActionBanner';



/**
 * One survey.
 *
 * On the **header** layout rather than the sidebar one: the record is a title, the divisions
 * and positions it covers, and its trail, read as one continuous thing rather than panels a reader chooses between.
 */
export function SurveyProfilePage() {
  const { id } = useParams<{ id: string }>();

  const [survey, setSurvey] = useState<Survey | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiError | null>(null);
  const [editing, setEditing] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [tab, setTab] = useState<TabKey>('info');
  const [configuration, setConfiguration] = useState<ConfigurationKey>('questionnaire');
  const [responsesView, setResponsesView] = useState<ResponsesKey>('summary');
  const [panelVersion, setPanelVersion] = useState(0);
  const [landedFor, setLandedFor] = useState<string | null>(null);

  const load = useCallback(
    (signal?: AbortSignal) => {
      if (!id) return;

      setLoading(true);

      surveysApi
        .get(id, signal)
        .then((next) => {
          if (!signal?.aborted) setSurvey(next);
        })
        .catch((caught: unknown) => {
          if (signal?.aborted) return;

          setError(
            caught instanceof ApiError ? caught : new ApiError('Could not load this survey.', 0),
          );
        })
        .finally(() => {
          if (!signal?.aborted) setLoading(false);
        });
    },
    [id],
  );

  /*
    The counts again, QUIETLY — after an answer, an assessment or a delete inside a panel. Not
    `load`: that skeletons the whole page and would drop the panel the reader is working in. A
    failed refresh leaves the page as it was.
  */
  const refreshCounts = useCallback(() => {
    if (!id) return;

    surveysApi
      .get(id)
      .then(setSurvey)
      .catch(() => undefined);
  }, [id]);

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);

    return () => controller.abort();
  }, [load]);

  // A plain string is a segment that goes nowhere; the link is the way back.
  const breadcrumb: BreadcrumbSegment[] = [
    'Learning & Development',
    { label: 'Survey', to: '/learning-development/survey' },
    survey ? survey.title : 'Survey',
  ];

  if (error) {
    return (
      <div className="flex h-full min-h-0 flex-col bg-slate-100">
        <Breadcrumb segments={breadcrumb} />
        <ErrorState
          message={error.message}
          correlationId={error.correlationId}
          onRetry={() => load()}
        />
      </div>
    );
  }

  /*
    A TAB NOT YET NEEDED IS DISABLED (product call, 12 Oct 2026) — for STATE, in the survey's own
    order: Responses has nothing until somebody responds, and the Development Plan nothing until a
    response is assessed. Both counts are the server's. If the tab the reader is on empties (its
    last response deleted), the page falls back to Configuration rather than sit on a dead tab.
  */
  /*
    THE PAGE LANDS ON THE NEXT THING TO DO (product call, 12 Oct 2026) — decided ONCE per record,
    when it first arrives, and never again: a refresh after a write must not move the reader. An
    adjustment during render, not an effect, so the first paint is already the right tab.
  */
  if (survey && landedFor !== survey.id) {
    const next = nextAction(survey);

    setLandedFor(survey.id);
    setTab(next.tab);
    setConfiguration(next.configuration);
    setResponsesView(next.responses);
  }

  const noResponses = (survey?.respondentCount ?? 0) === 0;
  const shown: TabKey =
    tab === 'responses' && noResponses ? 'configuration' : tab;

  if (loading || !survey) {
    return (
      <div className="flex h-full min-h-0 flex-col bg-slate-100">
        <Breadcrumb segments={breadcrumb} />
        <div className="flex flex-col gap-3 p-4">
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-col bg-slate-100">
      <Breadcrumb segments={breadcrumb} />

      <ProfileHeaderShell
        id="survey-profile"
        header={
          <ProfileHeader
            id="survey-profile-header"
            icon={<ClipboardList className="h-6 w-6" aria-hidden="true" />}
            title={survey.title}
            meta={[
              // Unlabelled, because "0 respondents" says what it is on its own.
              `${survey.respondentCount} ${survey.respondentCount === 1 ? 'respondent' : 'respondents'}`,
            ]}
            /*
              The header is THREE lines at most (product call, 09 Oct 2026): title, count, and
              ONE line of divisions · positions that ellipsizes, the whole of it in a <Tooltip>.
              Joined here as one fact rather than two, because two facts wrap onto a fourth line.
            */
            secondary={[
              <TargetsLine
                key="targets"
                text={`${describeTargets(survey.orgUnits, 'All units/organizations')} · ${describeTargets(survey.positions, 'All positions')}`}
              />,
            ]}
            status={
              <StatusText tone={survey.isActive ? 'active' : 'inactive'}>
                {survey.isActive ? 'Active' : 'Deleted'}
              </StatusText>
            }
            createdBy={survey.createdByName}
            createdAt={survey.createdAt}
            /*
              The record-scoped history, in the header's own cog and scoped with `entityId`. Its
              key is the audit module's, not this one's — a control carries the key of what it
              opens.
            */
            actions={
              <Menu
                id="survey-profile-settings"
                items={[
                  {
                    key: 'history',
                    label: 'History Logs',
                    icon: <History className="h-4 w-4" aria-hidden="true" />,
                    onClick: () => setHistoryOpen(true),
                    permission: 'audit-logs.read',
                  },
                ]}
                trigger={({ open, toggle }) => (
                  <button
                    type="button"
                    onClick={toggle}
                    aria-haspopup="menu"
                    aria-expanded={open}
                    aria-label="Survey settings"
                    className="rounded-lg p-1 text-slate-500 transition hover:bg-slate-100 hover:text-slate-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-200"
                  >
                    <Settings className="h-4 w-4" aria-hidden="true" />
                  </button>
                )}
              />
            }
          />
        }
        banner={
          /*
            THE BANNER NAMES THE NEXT THING TO DO (product call, 13 Oct 2026) and carries the way
            to do it, on the standard next-action banner. Unlike where the page LANDS, it follows
            the survey as it moves. A deleted survey's banner says so, with nothing to do.
          */
          <SurveyNextActionBanner
            survey={survey}
            at={{ tab: shown, configuration }}
            onGo={(next) => {
              setTab(next.tab);
              setConfiguration(next.configuration);
              setResponsesView(next.responses);
            }}
            onChanged={setSurvey}
            onSaved={() => {
              refreshCounts();
              // The open panel holds its own rows; remounting it is how it sees the new one.
              setPanelVersion((v) => v + 1);
            }}
          />
        }
        // The table owns its own chrome, so its tab runs edge to edge; Info keeps the card column.
        panel={shown === 'info' ? 'cards' : 'bleed'}
        tabs={[
          { key: 'info', label: 'Info' },
          // The page's own read key: the questionnaires are part of the survey.
          // What the survey is built from: its questionnaires, and the settings it was created with.
          { key: 'configuration', label: 'Configuration', permission: 'surveys.read' },
          {
            key: 'responses',
            permission: 'surveys.read',
            disabled: noResponses,
            label: (
              <>
                Responses <CountBadge count={survey.respondentCount} />
              </>
            ),
          },
          // Next to Responses: the employees assessed from them.
          // Anybody can be planned for (product call, 13 Oct 2026), so it no longer waits on an assessment.
          { key: 'plan', label: 'Development Plan', permission: 'surveys.read' },
        ]}
        activeTab={shown}
        onTabChange={setTab}
      >
        {shown === 'configuration' ? (
          /*
            A SECOND STRIP OF FOLDER TABS inside the profile's own — the product owner's call over
            reference §6's pills (09 Oct 2026). The SALN panel's shape — the strip over a bordered
            panel it merges into, sharing its left edge — WITHOUT the SALN box's slate-100 fill
            (product call, the same day: the grey ground read as a shadow around the table). The
            cost, accepted: the strip's ground is white, lighter than the inactive tabs' slate-50.
          */
          <div className="flex h-full min-h-0 min-w-0 flex-col p-4">
            <div className="flex min-h-0 flex-1 flex-col">
              <FolderTabs<ConfigurationKey>
                id="survey-configuration"
                ariaLabel="Configuration"
                paddingX="px-3"
                activeKey={configuration}
                onChange={setConfiguration}
                tabs={[
                  { key: 'questionnaire', label: 'Questionnaire' },
                ]}
              />

              {/*
                Bordered on ALL FOUR sides, as `<ProfileHeaderShell>`'s own panel is: the top
                border is the line running past the tabs, and the active tab's white bottom edge
                breaks it to merge in. (The SALN box's `border-t-0` relied on its slate fill to hide
                that the line stopped at the last tab — without the fill, the top was open.)
              */}
              <div className="min-h-0 flex-1 rounded-b-panel border border-slate-300 bg-white">
                <SurveyQuestionnairesPanel
                  key={panelVersion}
                  surveyId={survey.id}
                  surveyActive={survey.isActive}
                  surveyStatus={survey.status}
                  onChanged={refreshCounts}
                  onSurveyChanged={setSurvey}
                />
              </div>
            </div>
          </div>
        ) : shown === 'responses' ? (
          <SurveyResponsesPanel
            surveyId={survey.id}
            surveyActive={survey.isActive}
            initialView={responsesView}
            onChanged={refreshCounts}
          />
        ) : shown === 'plan' ? (
          <SurveyDevelopmentPlanPanel key={panelVersion} surveyId={survey.id} onChanged={refreshCounts} />
        ) : (
          <>
            <ProfileCard
              id="survey-details"
              title="Details"
              menuItems={[
                {
                  key: 'edit',
                  label: 'Edit',
                  permission: 'surveys.update',
                  onClick: () => setEditing(true),
                },
              ]}
            >
              <PreviewTable
                variant="table-display"
                rows={[
                  { label: 'Title', value: survey.title },
                  { label: 'Units/Organization', value: describeTargets(survey.orgUnits, 'All units/organizations') },
                  { label: 'Position', value: describeTargets(survey.positions, 'All positions') },
                  {
                    label: 'No. of Respondents',
                    value: <span className="font-mono tabular-nums">{survey.respondentCount}</span>,
                  },
                ]}
              />
            </ProfileCard>

            {/*
              Where the survey is in its run. It sat on Configuration › Settings until that tab was
              removed (product call, 13 Oct 2026); the facts are kept here, and its Start / End /
              Re-open are also on the banner and the questionnaire's cog.
            */}
            <SurveyStatusCard survey={survey} onChanged={setSurvey} />

            {/* Audit is never editable: created and updated are a record of what happened. */}
            <ProfileCard id="survey-audit" title="Audit">
              <PreviewTable
                variant="table-display"
                rows={[
                  { label: 'Created by', value: survey.createdByName },
                  {
                    label: 'Created',
                    value: (
                      <time dateTime={toIso(survey.createdAt)} className="font-mono">
                        {formatDateTime(survey.createdAt)}
                      </time>
                    ),
                  },
                  { label: 'Updated by', value: survey.updatedByName },
                  {
                    label: 'Updated',
                    value: survey.updatedAt ? (
                      <time dateTime={toIso(survey.updatedAt)} className="font-mono">
                        {formatDateTime(survey.updatedAt)}
                      </time>
                    ) : null,
                  },
                ]}
              />
            </ProfileCard>
          </>
        )}
      </ProfileHeaderShell>

      {editing ? (
        <SurveyModal
          open
          editing={survey}
          onClose={() => setEditing(false)}
          onSaved={() => {
            setEditing(false);
            load();
          }}
        />
      ) : null}

      <HistoryLogsModal
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        module="surveys"
        entityTypes={['Survey', 'SurveyOrgUnit', 'SurveyPosition', 'SurveyQuestionnaire', 'SurveyRespondent']}
        entityId={survey.id}
      />
    </div>
  );
}

/** The header's divisions · positions — one line, ellipsized, the whole of it in the tooltip. */
function TargetsLine({ text }: { text: string }) {
  return (
    <Tooltip label={text} block>
      <span className="block truncate">{text}</span>
    </Tooltip>
  );
}
